import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchServerStatus } from "../components/ServerStatusWidget";
import {
  Server, Trophy, UserPlus, Headphones, Gamepad2, ArrowRight,
  Map, Users, Play as PlayIcon, Copy, Check, Lock, Radio, Swords, Gift, ShieldCheck, Zap
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Avatar, Username } from "../components/UserBadge";

// Arena Components
import MatchmakingConsole from "../components/arena/MatchmakingConsole";
import MatchFoundModal from "../components/arena/MatchFoundModal";
import MatchLobbyView from "../components/arena/MatchLobbyView";
import CaseOpeningLab from "../components/arena/CaseOpeningLab";

export default function Play() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("arena"); // 'arena' | 'cases' | 'leaderboard' | 'servers'

  // Poll Arena Status every 2.5 seconds
  const { data: arenaStatus } = useQuery({
    queryKey: ["arenaStatus"],
    queryFn: async () => (await api.get("/arena/status")).data,
    refetchInterval: 2500,
    staleTime: 0
  });

  // Arena Leaderboard
  const { data: leaderboard = [] } = useQuery({
    queryKey: ["arenaLeaderboard"],
    queryFn: async () => (await api.get("/arena/leaderboard")).data,
    staleTime: 30000,
    enabled: activeTab === "leaderboard"
  });

  // Join Queue Mutation
  const joinQueueMutation = useMutation({
    mutationFn: async ({ mode, region }) => (await api.post("/arena/queue/join", { mode, region })).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries(["arenaStatus"]);
      toast.success(`Ai intrat în coada ${data.mode?.toUpperCase()}!`);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Eroare la intrarea în coadă.")
  });

  // Leave Queue Mutation
  const leaveQueueMutation = useMutation({
    mutationFn: async () => (await api.post("/arena/queue/leave")).data,
    onSuccess: () => {
      queryClient.invalidateQueries(["arenaStatus"]);
      toast.info("Ai părăsit coada de căutare.");
    }
  });

  // Accept Match Mutation
  const acceptMatchMutation = useMutation({
    mutationFn: async (matchId) => (await api.post(`/arena/matches/${matchId}/accept`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries(["arenaStatus"]);
      toast.success("Meci acceptat!");
    }
  });

  const isSearching = Boolean(arenaStatus?.user_queue);
  const activeMatch = arenaStatus?.user_match;
  const isMatchPending = activeMatch && activeMatch.status === "PENDING_ACCEPT";
  const isMatchLive = activeMatch && (activeMatch.status === "LIVE" || activeMatch.status === "COMPLETED");

  return (
    <div className="max-w-[1500px] mx-auto space-y-6">
      {/* POPUP MODAL ON MATCH FOUND */}
      {isMatchPending && (
        <MatchFoundModal
          match={activeMatch}
          user={user}
          onAccept={(mId) => acceptMatchMutation.mutate(mId)}
        />
      )}

      {/* 1. Hero Command Header */}
      <div className="bg-[#0b0e15] border border-white/10 p-4 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-80 h-full bg-gradient-to-l from-[var(--tf-primary)]/10 via-transparent to-transparent pointer-events-none" />
        <div className="flex items-center gap-3 relative z-10">
          <div 
            className="w-12 h-12 flex items-center justify-center text-black font-black shrink-0 shadow-lg"
            style={{ backgroundColor: "var(--tf-primary)" }}
          >
            <Swords className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-chakra text-2xl sm:text-3xl font-black uppercase tracking-wider text-white">
              ARENA 5v5 & MATCHMAKING
            </h1>
            <p className="text-xs text-zinc-400 font-mono">
              // Cozi competitive CS2, Map Veto, clasamente ELO și deschidere cutii
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 relative z-10">
          <div className="px-3.5 py-1.5 bg-[#07090e] border border-white/10 flex items-center gap-2 text-xs font-mono">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-zinc-400">Jucători în coadă:</span>
            <span className="font-chakra font-bold text-white text-sm">{arenaStatus?.total_queued || 0}</span>
          </div>
        </div>
      </div>

      {/* 2. Top Arena Navigation Tabs */}
      <div className="bg-[#0b0e15] border border-white/10 p-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setActiveTab("arena")}
            className={`px-4 py-2 text-xs font-chakra font-black uppercase tracking-wider flex items-center gap-2 transition-all border ${
              activeTab === "arena" 
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md" 
                : "bg-[#07090e] border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            <Swords className="w-4 h-4" />
            <span>Matchmaking Arena</span>
          </button>

          <button
            onClick={() => setActiveTab("cases")}
            className={`px-4 py-2 text-xs font-chakra font-black uppercase tracking-wider flex items-center gap-2 transition-all border ${
              activeTab === "cases" 
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md" 
                : "bg-[#07090e] border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            <Gift className="w-4 h-4" />
            <span>Case Opening Lab</span>
          </button>

          <button
            onClick={() => setActiveTab("leaderboard")}
            className={`px-4 py-2 text-xs font-chakra font-black uppercase tracking-wider flex items-center gap-2 transition-all border ${
              activeTab === "leaderboard" 
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md" 
                : "bg-[#07090e] border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            <Trophy className="w-4 h-4" />
            <span>Clasament ELO</span>
          </button>

          <button
            onClick={() => setActiveTab("servers")}
            className={`px-4 py-2 text-xs font-chakra font-black uppercase tracking-wider flex items-center gap-2 transition-all border ${
              activeTab === "servers" 
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md" 
                : "bg-[#07090e] border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            <Server className="w-4 h-4" />
            <span>Servere Comunitate</span>
          </button>
        </div>

        <div className="flex items-center gap-2 px-3 text-xs font-mono text-zinc-400">
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <span>Meciuri Active: <strong className="text-white font-chakra">{arenaStatus?.live_matches?.length || 0}</strong></span>
        </div>
      </div>

      {/* TAB 1: ARENA & MATCHMAKING */}
      {activeTab === "arena" && (
        <div className="space-y-6">
          {/* If user is inside a live or completed match, render Lobby View */}
          {isMatchLive ? (
            <MatchLobbyView match={activeMatch} user={user} />
          ) : (
            <MatchmakingConsole
              user={user}
              statusData={arenaStatus}
              isSearching={isSearching}
              onJoinQueue={(mode, region) => joinQueueMutation.mutate({ mode, region })}
              onLeaveQueue={() => leaveQueueMutation.mutate()}
            />
          )}

          {/* Live Matches Feed on INDUNGI Arena */}
          <div className="p-6 bg-[#0b0e15] border border-white/10 space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div>
                <h3 className="text-sm font-black uppercase text-white font-chakra flex items-center gap-2">
                  <Radio className="w-4 h-4 text-red-500 animate-pulse" /> Meciuri Live pe Arena INDUNGI
                </h3>
                <p className="text-xs text-zinc-400 font-mono mt-0.5">Scoruri transmise în timp real de pe serverele de meci.</p>
              </div>
              <span className="text-xs font-mono text-zinc-400 bg-white/5 px-2.5 py-1 border border-white/10">
                Meciuri active: <strong className="text-white">{arenaStatus?.live_matches?.length || 0}</strong>
              </span>
            </div>

            {(!arenaStatus?.live_matches || arenaStatus.live_matches.length === 0) ? (
              <div className="py-12 text-center text-zinc-500 font-mono text-xs">
                <Swords className="w-8 h-8 mx-auto mb-2 opacity-30 text-[var(--tf-primary)]" />
                <p>Niciun meci live în desfășurare chiar acum. Intră în coadă pentru a începe unul!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {arenaStatus.live_matches.map((m) => (
                  <div key={m.id} className="p-4 border border-white/10 bg-[#090c12] hover:bg-[#0e131d] transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-black uppercase px-2 py-0.5 bg-orange-500/20 text-orange-400 border border-orange-500/30 font-chakra">
                        {m.mode?.toUpperCase()}
                      </span>
                      <span className="text-xs font-mono font-bold text-white uppercase">{m.map}</span>
                    </div>

                    <div className="flex items-center justify-between my-2">
                      <span className="text-xs font-bold font-chakra text-sky-400 truncate max-w-[100px]">{m.team1_name}</span>
                      <div className="flex items-center gap-2 px-3 py-1 bg-black font-mono font-black text-sm border border-white/10">
                        <span className="text-sky-400">{m.score_ct}</span>
                        <span className="text-zinc-600">:</span>
                        <span className="text-amber-500">{m.score_t}</span>
                      </div>
                      <span className="text-xs font-bold font-chakra text-amber-500 truncate max-w-[100px] text-right">{m.team2_name}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: CASE OPENING */}
      {activeTab === "cases" && (
        <CaseOpeningLab user={user} />
      )}

      {/* TAB 3: LEADERBOARD */}
      {activeTab === "leaderboard" && (
        <div className="p-6 bg-[#0b0e15] border border-white/10 space-y-6">
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <div>
              <div className="flex items-center gap-3">
                <Trophy className="w-5 h-5 text-amber-400" />
                <h2 className="text-base font-black uppercase text-white font-chakra">Clasament Competitiv ELO (VultStrike)</h2>
              </div>
              <p className="text-xs text-zinc-400 font-mono mt-1">Cei mai buni jucători de CS2 din comunitatea INDUNGI pe baza rating-ului.</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-mono">
              <thead>
                <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-wider text-[11px]">
                  <th className="pb-3 pl-4"># Rang</th>
                  <th className="pb-3">Jucător</th>
                  <th className="pb-3">Rank CS2</th>
                  <th className="pb-3 text-center">Victorii</th>
                  <th className="pb-3 text-center">Winrate</th>
                  <th className="pb-3 text-right pr-4">Puncte ELO</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {leaderboard.map((u, i) => (
                  <tr key={u.id} className="hover:bg-[#0e131d] transition-colors">
                    <td className="py-3.5 pl-4 font-bold text-sm font-chakra">
                      {i === 0 ? "🥇 1" : i === 1 ? "🥈 2" : i === 2 ? "🥉 3" : `${i + 1}`}
                    </td>
                    <td className="py-3.5">
                      <div className="flex items-center gap-3">
                        <Avatar user={u} size={36} hover={true} />
                        <div>
                          <Username user={u} className="font-bold text-sm font-chakra" />
                          <span className="text-[10px] text-zinc-500 block">INDUNGI PRO</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5">
                      <span className="px-2.5 py-1 bg-white/5 border border-white/10 font-bold text-white uppercase text-[10px] font-chakra">
                        {u.rank}
                      </span>
                    </td>
                    <td className="py-3.5 text-center font-bold text-zinc-300">{u.matches_won}</td>
                    <td className="py-3.5 text-center font-bold text-emerald-400">{u.winrate}</td>
                    <td className="py-3.5 text-right pr-4 font-black text-orange-400 text-sm font-chakra">
                      {u.elo} ELO
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: CLASSIC COMMUNITY SERVERS */}
      {activeTab === "servers" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-[#0b0e15] border border-white/10 p-4">
            <div>
              <h2 className="text-base font-black uppercase text-white font-chakra">Servere Dedicate Comunitate</h2>
              <p className="text-xs text-zinc-400 font-mono">Servere publice clasice CS 1.6 & Mixuri cu conectare rapidă prin Steam.</p>
            </div>
            <Link
              to="/servere"
              className="tf-btn-primary tf-clip-btn px-4 py-2 text-xs font-black uppercase tracking-wider"
            >
              VEZI TOATE SERVERELOR
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { id: "s1", name: "OFFICIAL.INDUNGI.PRO # CS 1.6 CLASIC", address: "209.38.247.243:27015", game_type: "cs16" },
              { id: "s2", name: "DOPE.INDUNGI.PRO # ROMANIAN LEGENDS", address: "209.38.247.243:27016", game_type: "cs16" },
              { id: "s3", name: "MIX.INDUNGI.PRO # 5v5 WAR SERVER", address: "209.38.247.243:27017", game_type: "mix" }
            ].map((srv) => (
              <div key={srv.id} className="p-5 border border-white/10 bg-[#0b0e15] hover:border-[var(--tf-primary)]/60 transition-all space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono font-bold text-emerald-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> LIVE
                  </span>
                  <span className="text-[10px] uppercase font-bold text-zinc-500 font-chakra">{srv.game_type?.toUpperCase()}</span>
                </div>
                <h3 className="text-sm font-black uppercase text-white font-chakra truncate">{srv.name}</h3>
                <div className="p-2.5 bg-[#07090e] border border-white/10 font-mono text-xs text-zinc-400 truncate">
                  {srv.address}
                </div>
                <button
                  onClick={() => window.location.href = `steam://connect/${srv.address}`}
                  className="tf-btn-primary tf-clip-btn w-full py-2.5 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2"
                >
                  <PlayIcon className="w-3.5 h-3.5 fill-current" />
                  <span>CONECTARE STEAM</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
